import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Typography, Button, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Paper, TextField, IconButton, CircularProgress,
  Dialog, DialogTitle, DialogContent, DialogActions, MenuItem, Alert,
  Chip, Card, CardContent, Grid, Tooltip, InputAdornment,
  TablePagination, TableSortLabel, Checkbox, FormControl, InputLabel, Select,
  Menu, Switch, FormControlLabel,
} from '@mui/material';
import {
  Add as AddIcon, Search as SearchIcon,
  Edit as EditIcon, Delete as DeleteIcon,
  PeopleAlt as EmptyIcon, FileDownload as ExportIcon,
  MoreVert as MoreVertIcon, Visibility as ViewIcon,
} from '@mui/icons-material';
import { api } from '../services/apiClient';
import { API_ENDPOINTS } from '../config/api';

interface Client {
  id: string;
  first_name: string;
  last_name: string;
  email: string;
  phone_primary: string;
  phone_secondary?: string;
  address_line1?: string;
  address_line2?: string;
  city?: string;
  state_province?: string;
  postal_code?: string;
  country?: string;
  preferred_communication?: string;
  marketing_consent_email?: number;
  marketing_consent_sms?: number;
  is_active: number;
  created_at?: string;
  pets_summary?: string;
  pet_count?: number;
  company_name?: string;
  notes?: string;
}

const emptyForm = {
  first_name: '', last_name: '', email: '', phone_primary: '',
  phone_secondary: '', address_line1: '', address_line2: '', city: '',
  state_province: '', postal_code: '', country: '',
  preferred_communication: 'email',
  marketing_consent_email: false,
  marketing_consent_sms: false,
  notes: '',
  company_name: '',
  client_type: 'individual',
};

type FormShape = typeof emptyForm;

const fmtClientId = (id: string | number) => `#CL-${String(id).padStart(6, '0')}`;
const fmtDate = (d?: string | null) => {
  if (!d) return '—';
  const dt = new Date(d);
  if (isNaN(dt.getTime())) return '—';
  return dt.toLocaleDateString('en-AU', { day: '2-digit', month: '2-digit', year: 'numeric' });
};

type SortField = 'name' | 'email' | 'joined' | 'pets' | 'status';

const exportCSV = (data: Client[], filename = 'clients.csv') => {
  const headers = ['Client ID', 'First Name', 'Last Name', 'Email', 'Phone', 'Joined', 'Pets', 'Status'];
  const rows = data.map(c => [
    fmtClientId(c.id), c.first_name, c.last_name, c.email ?? '', c.phone_primary ?? '',
    fmtDate(c.created_at), String(c.pet_count ?? 0), c.is_active === 1 ? 'Active' : 'Inactive',
  ]);
  const csv = [headers, ...rows]
    .map(r => r.map(v => `"${String(v ?? '').replace(/"/g, '""')}"`).join(','))
    .join('\n');
  const blob = new Blob([csv], { type: 'text/csv' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url; a.download = filename; a.click();
  URL.revokeObjectURL(url);
};

// Species icon map — extend as needed
const speciesIcon = (petName: string): string => {
  const lower = petName.toLowerCase();
  if (lower.includes('(cat)') || lower.includes('cat')) return '🐱';
  if (lower.includes('(dog)') || lower.includes('dog')) return '🐶';
  if (lower.includes('rabbit')) return '🐰';
  if (lower.includes('bird') || lower.includes('parrot')) return '🐦';
  if (lower.includes('fish')) return '🐟';
  return '🐾';
};

const PetTags = ({ summary }: { summary?: string }) => {
  if (!summary) return <Typography variant="body2" color="text.disabled">—</Typography>;
  const pets = summary.split(', ');
  const shown = pets.slice(0, 2);
  const extra = pets.slice(2);
  return (
    <Box display="flex" gap={0.5} flexWrap="wrap" alignItems="center">
      {shown.map((pet, i) => (
        <Chip key={i}
          icon={<span style={{ fontSize: 12, lineHeight: 1 }}>{speciesIcon(pet)}</span>}
          label={pet.replace(/\s*\(.*?\)\s*/g, '')}
          size="small" color="primary" variant="outlined"
          sx={{ fontSize: 11, height: 22 }} />
      ))}
      {extra.length > 0 && (
        <Tooltip title={extra.join(', ')}>
          <Chip label={`+${extra.length} more`} size="small" variant="outlined"
            sx={{ fontSize: 11, height: 22 }} />
        </Tooltip>
      )}
    </Box>
  );
};

const StatCard = ({ label, value, color }: { label: string; value: number; color: string }) => (
  <Card variant="outlined">
    <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
      <Typography variant="caption" color="text.secondary" display="block">{label}</Typography>
      <Typography variant="h5" color={color} fontWeight={700}>{value}</Typography>
    </CardContent>
  </Card>
);

const ClientFormFields = ({ f, setF }: { f: FormShape; setF: (v: FormShape) => void }) => (
  <Box display="flex" flexDirection="column" gap={2} mt={1}>
    <Box display="flex" gap={2}>
      <TextField label="First Name *" fullWidth value={f.first_name}
        onChange={e => setF({ ...f, first_name: e.target.value })} />
      <TextField label="Last Name *" fullWidth value={f.last_name}
        onChange={e => setF({ ...f, last_name: e.target.value })} />
    </Box>
    <TextField label="Email" type="email" fullWidth value={f.email}
      onChange={e => setF({ ...f, email: e.target.value })} />
    <Box display="flex" gap={2}>
      <TextField label="Primary Phone" fullWidth value={f.phone_primary}
        onChange={e => setF({ ...f, phone_primary: e.target.value })} />
      <TextField label="Secondary / Emergency Phone" fullWidth value={f.phone_secondary}
        onChange={e => setF({ ...f, phone_secondary: e.target.value })}
        placeholder="Backup contact number" />
    </Box>
    <TextField label="Address Line 1" fullWidth value={f.address_line1}
      onChange={e => setF({ ...f, address_line1: e.target.value })} />
    <Box display="flex" gap={2}>
      <TextField label="City" fullWidth value={f.city}
        onChange={e => setF({ ...f, city: e.target.value })} />
      <TextField label="State / Province" fullWidth value={f.state_province}
        onChange={e => setF({ ...f, state_province: e.target.value })} />
    </Box>
    <Box display="flex" gap={2}>
      <TextField label="Zip / Postal Code" fullWidth value={f.postal_code}
        onChange={e => setF({ ...f, postal_code: e.target.value })} />
      <TextField label="Country" fullWidth value={f.country}
        onChange={e => setF({ ...f, country: e.target.value })} />
    </Box>
    <TextField select label="Preferred Communication" fullWidth value={f.preferred_communication}
      onChange={e => setF({ ...f, preferred_communication: e.target.value })}>
      <MenuItem value="email">Email</MenuItem>
      <MenuItem value="sms">SMS</MenuItem>
      <MenuItem value="phone">Phone</MenuItem>
    </TextField>
    <Box sx={{ border: 1, borderColor: 'divider', borderRadius: 1, px: 2, py: 1 }}>
      <Typography variant="caption" color="text.secondary" display="block" mb={0.5}>
        Marketing &amp; Reminder Consent
      </Typography>
      <FormControlLabel
        control={
          <Switch size="small" checked={!!f.marketing_consent_email}
            onChange={e => setF({ ...f, marketing_consent_email: e.target.checked })} />
        }
        label={<Typography variant="body2">Email reminders &amp; marketing</Typography>}
      />
      <FormControlLabel
        control={
          <Switch size="small" checked={!!f.marketing_consent_sms}
            onChange={e => setF({ ...f, marketing_consent_sms: e.target.checked })} />
        }
        label={<Typography variant="body2">SMS reminders &amp; marketing</Typography>}
      />
    </Box>
    <TextField label="Notes" fullWidth multiline rows={2} value={f.notes}
      onChange={e => setF({ ...f, notes: e.target.value })} />
    <TextField select label="Client Type" fullWidth value={f.client_type || 'individual'}
      onChange={e => setF({ ...f, client_type: e.target.value })}>
      <MenuItem value="individual">Individual</MenuItem>
      <MenuItem value="business">Business</MenuItem>
    </TextField>
    <TextField label="Company Name" fullWidth value={f.company_name || ''}
      onChange={e => setF({ ...f, company_name: e.target.value })} />
  </Box>
);

// Per-row kebab menu
const RowMenu = ({
  onView,
  onEdit,
  onDelete,
}: {
  onView: () => void;
  onEdit: () => void;
  onDelete: () => void;
}) => {
  const [anchor, setAnchor] = useState<null | HTMLElement>(null);
  const close = () => setAnchor(null);
  return (
    <>
      <IconButton size="small" onClick={e => { e.stopPropagation(); setAnchor(e.currentTarget); }}>
        <MoreVertIcon fontSize="small" />
      </IconButton>
      <Menu anchorEl={anchor} open={!!anchor} onClose={() => { close(); }}
        onClick={e => e.stopPropagation()}>
        <MenuItem onClick={() => { close(); onView(); }}>
          <ViewIcon fontSize="small" sx={{ mr: 1 }} /> View Details
        </MenuItem>
        <MenuItem onClick={() => { close(); onEdit(); }}>
          <EditIcon fontSize="small" sx={{ mr: 1 }} /> Edit Client
        </MenuItem>
        <MenuItem onClick={() => { close(); onDelete(); }} sx={{ color: 'error.main' }}>
          <DeleteIcon fontSize="small" sx={{ mr: 1 }} /> Delete Client
        </MenuItem>
      </Menu>
    </>
  );
};

const ClientsPage = () => {
  const navigate = useNavigate();
  const [clients, setClients] = useState<Client[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<'all' | 'active' | 'inactive'>('all');
  const [sortField, setSortField] = useState<SortField>('name');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc');
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);

  // Create
  const [createOpen, setCreateOpen] = useState(false);
  const [form, setForm] = useState<FormShape>(emptyForm);
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);

  // Edit
  const [editClient, setEditClient] = useState<Client | null>(null);
  const [editForm, setEditForm] = useState<FormShape>(emptyForm);
  const [editSaving, setEditSaving] = useState(false);
  const [editError, setEditError] = useState<string | null>(null);

  // Archive/delete
  const [archiveClient, setArchiveClient] = useState<Client | null>(null);
  const [archiving, setArchiving] = useState(false);

  // Bulk delete confirm
  const [bulkDeleteOpen, setBulkDeleteOpen] = useState(false);
  const [bulkDeleting, setBulkDeleting] = useState(false);

  const [globalError, setGlobalError] = useState<string | null>(null);

  const fetchClients = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get(`${API_ENDPOINTS.CLIENTS.LIST}?limit=500`);
      setClients(res.data || res || []);
    } catch {
      setGlobalError('Failed to load clients');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchClients(); }, [fetchClients]);

  // Stats
  const thisMonth = new Date().toISOString().slice(0, 7);
  const stats = {
    total: clients.length,
    active: clients.filter(c => c.is_active === 1).length,
    inactive: clients.filter(c => c.is_active !== 1).length,
    newThisMonth: clients.filter(c => c.created_at?.slice(0, 7) === thisMonth).length,
  };

  // Sort handler
  const handleSort = (field: SortField) => {
    if (sortField === field) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortField(field); setSortDir('asc'); }
    setPage(0);
  };

  // Filter + sort + paginate
  const filtered = clients
    .filter(c => {
      const matchSearch = `${c.first_name} ${c.last_name} ${c.email} ${c.phone_primary ?? ''}`.toLowerCase()
        .includes(search.toLowerCase());
      const matchStatus = statusFilter === 'all' || (statusFilter === 'active' ? c.is_active === 1 : c.is_active !== 1);
      return matchSearch && matchStatus;
    })
    .sort((a, b) => {
      const dir = sortDir === 'asc' ? 1 : -1;
      if (sortField === 'name') return dir * `${a.last_name} ${a.first_name}`.localeCompare(`${b.last_name} ${b.first_name}`);
      if (sortField === 'email') return dir * (a.email ?? '').localeCompare(b.email ?? '');
      if (sortField === 'joined') return dir * (a.created_at ?? '').localeCompare(b.created_at ?? '');
      if (sortField === 'pets') return dir * ((a.pet_count ?? 0) - (b.pet_count ?? 0));
      if (sortField === 'status') return dir * ((a.is_active ?? 0) - (b.is_active ?? 0));
      return 0;
    });
  const paginated = filtered.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  // Multi-select helpers
  const allPageSelected = paginated.length > 0 && paginated.every(c => selected.has(c.id));
  const somePageSelected = paginated.some(c => selected.has(c.id));
  const someSelected = selected.size > 0;
  const toggleAll = () => {
    const next = new Set(selected);
    if (allPageSelected) { paginated.forEach(c => next.delete(c.id)); }
    else { paginated.forEach(c => next.add(c.id)); }
    setSelected(next);
  };
  const toggleOne = (id: string) => {
    const next = new Set(selected);
    next.has(id) ? next.delete(id) : next.add(id);
    setSelected(next);
  };

  const handleBulkDelete = async () => {
    setBulkDeleting(true);
    for (const id of selected) {
      await api.delete(API_ENDPOINTS.CLIENTS.DELETE(id)).catch(() => {});
    }
    setBulkDeleting(false);
    setBulkDeleteOpen(false);
    setSelected(new Set());
    fetchClients();
  };

  const openEdit = (client: Client) => {
    setEditClient(client);
    setEditForm({
      first_name: client.first_name,
      last_name: client.last_name,
      email: client.email,
      phone_primary: client.phone_primary ?? '',
      phone_secondary: client.phone_secondary ?? '',
      address_line1: client.address_line1 ?? '',
      address_line2: client.address_line2 ?? '',
      city: client.city ?? '',
      state_province: client.state_province ?? '',
      postal_code: client.postal_code ?? '',
      country: client.country ?? '',
      preferred_communication: client.preferred_communication ?? 'email',
      marketing_consent_email: !!(client.marketing_consent_email),
      marketing_consent_sms: !!(client.marketing_consent_sms),
      notes: client.notes ?? '',
      company_name: client.company_name ?? '',
      client_type: 'individual',
    });
    setEditError(null);
  };

  const handleCreate = async () => {
    if (!form.first_name || !form.last_name) {
      setCreateError('First name and last name are required.');
      return;
    }
    setCreating(true);
    setCreateError(null);
    try {
      await api.post(API_ENDPOINTS.CLIENTS.CREATE, {
        ...form,
        marketing_consent_email: form.marketing_consent_email ? 1 : 0,
        marketing_consent_sms: form.marketing_consent_sms ? 1 : 0,
      });
      setCreateOpen(false);
      setForm(emptyForm);
      fetchClients();
    } catch (err: any) {
      setCreateError(err?.response?.data?.error?.message || err?.message || 'Failed to create client.');
    } finally {
      setCreating(false);
    }
  };

  const handleEdit = async () => {
    if (!editClient || !editForm.first_name || !editForm.last_name) {
      setEditError('First name and last name are required.');
      return;
    }
    setEditSaving(true);
    setEditError(null);
    try {
      await api.put(API_ENDPOINTS.CLIENTS.UPDATE(editClient.id), {
        ...editForm,
        marketing_consent_email: editForm.marketing_consent_email ? 1 : 0,
        marketing_consent_sms: editForm.marketing_consent_sms ? 1 : 0,
      });
      setEditClient(null);
      fetchClients();
    } catch (err: any) {
      setEditError(err?.response?.data?.error?.message || 'Failed to update client.');
    } finally {
      setEditSaving(false);
    }
  };

  const handleArchive = async () => {
    if (!archiveClient) return;
    setArchiving(true);
    try {
      await api.delete(API_ENDPOINTS.CLIENTS.DELETE(archiveClient.id));
      setArchiveClient(null);
      fetchClients();
    } catch {
      setGlobalError('Failed to archive client.');
    } finally {
      setArchiving(false);
    }
  };

  if (loading) return (
    <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
      <CircularProgress />
    </Box>
  );

  return (
    <Box>
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="flex-end" mb={2}>
        <Box>
          <Typography variant="caption" color="text.secondary">
            Vet Clinic &rsaquo; <strong>Clients</strong>
          </Typography>
          <Typography variant="h4" fontWeight={700}>Clients</Typography>
        </Box>
        <Box display="flex" gap={1}>
          <Tooltip title="Export filtered clients as CSV">
            <Button variant="outlined" startIcon={<ExportIcon />}
              onClick={() => exportCSV(filtered)}>
              Export
            </Button>
          </Tooltip>
          <Tooltip title="Create new client record">
            <Button variant="contained" startIcon={<AddIcon />}
              onClick={() => { setForm(emptyForm); setCreateError(null); setCreateOpen(true); }}>
              New Client
            </Button>
          </Tooltip>
        </Box>
      </Box>

      {globalError && (
        <Alert severity="error" onClose={() => setGlobalError(null)} sx={{ mb: 2 }}>{globalError}</Alert>
      )}

      {/* Stats cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={6} sm={3}>
          <StatCard label="Total Clients" value={stats.total} color="text.primary" />
        </Grid>
        <Grid item xs={6} sm={3}>
          <StatCard label="Active" value={stats.active} color="success.main" />
        </Grid>
        <Grid item xs={6} sm={3}>
          <StatCard label="Inactive" value={stats.inactive} color="text.secondary" />
        </Grid>
        <Grid item xs={6} sm={3}>
          <StatCard label="New This Month" value={stats.newThisMonth} color="primary.main" />
        </Grid>
      </Grid>

      {/* Filter bar */}
      <Box display="flex" gap={2} alignItems="center" mb={2}>
        <TextField
          variant="outlined" size="small" placeholder="Search by name, email or phone..."
          value={search}
          onChange={e => { setSearch(e.target.value); setPage(0); }}
          sx={{ flex: 1 }}
          InputProps={{
            startAdornment: <InputAdornment position="start"><SearchIcon color="action" /></InputAdornment>,
          }}
        />
        <FormControl size="small" sx={{ minWidth: 140 }}>
          <InputLabel>Status</InputLabel>
          <Select value={statusFilter} label="Status"
            onChange={e => { setStatusFilter(e.target.value as 'all' | 'active' | 'inactive'); setPage(0); }}>
            <MenuItem value="all">All</MenuItem>
            <MenuItem value="active">Active</MenuItem>
            <MenuItem value="inactive">Inactive</MenuItem>
          </Select>
        </FormControl>
      </Box>

      {/* Floating selection toolbar */}
      {someSelected && (
        <Box sx={{
          display: 'flex', alignItems: 'center', gap: 2, px: 2, py: 1, mb: 1,
          bgcolor: 'primary.main', color: 'primary.contrastText', borderRadius: 1,
        }}>
          <Typography variant="body2" fontWeight={600} sx={{ flex: 1 }}>
            {selected.size} client{selected.size !== 1 ? 's' : ''} selected
          </Typography>
          <Button size="small" variant="outlined"
            sx={{ color: 'inherit', borderColor: 'rgba(255,255,255,0.5)' }}
            startIcon={<ExportIcon />}
            onClick={() => exportCSV(clients.filter(c => selected.has(c.id)), 'selected-clients.csv')}>
            Export
          </Button>
          <Button size="small" variant="outlined"
            sx={{ color: 'inherit', borderColor: 'rgba(255,255,255,0.5)' }}
            startIcon={<DeleteIcon />}
            onClick={() => setBulkDeleteOpen(true)}>
            Delete
          </Button>
          <Button size="small"
            sx={{ color: 'inherit' }}
            onClick={() => setSelected(new Set())}>
            Clear
          </Button>
        </Box>
      )}

      {/* Table */}
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell padding="checkbox">
                <Checkbox
                  size="small"
                  checked={allPageSelected}
                  indeterminate={somePageSelected && !allPageSelected}
                  onChange={toggleAll}
                />
              </TableCell>
              <TableCell sx={{ color: 'text.secondary', fontSize: 12, width: 110 }}>Client ID</TableCell>
              <TableCell>
                <TableSortLabel
                  active={sortField === 'name'}
                  direction={sortField === 'name' ? sortDir : 'asc'}
                  onClick={() => handleSort('name')}>
                  Name
                </TableSortLabel>
              </TableCell>
              <TableCell>
                <TableSortLabel
                  active={sortField === 'email'}
                  direction={sortField === 'email' ? sortDir : 'asc'}
                  onClick={() => handleSort('email')}>
                  Email
                </TableSortLabel>
              </TableCell>
              <TableCell>Phone</TableCell>
              <TableCell>
                <TableSortLabel
                  active={sortField === 'joined'}
                  direction={sortField === 'joined' ? sortDir : 'asc'}
                  onClick={() => handleSort('joined')}>
                  Joined
                </TableSortLabel>
              </TableCell>
              <TableCell>
                <TableSortLabel
                  active={sortField === 'pets'}
                  direction={sortField === 'pets' ? sortDir : 'asc'}
                  onClick={() => handleSort('pets')}>
                  Pets
                </TableSortLabel>
              </TableCell>
              <TableCell>
                <TableSortLabel
                  active={sortField === 'status'}
                  direction={sortField === 'status' ? sortDir : 'asc'}
                  onClick={() => handleSort('status')}>
                  Status
                </TableSortLabel>
              </TableCell>
              <TableCell align="right" width={60} />
            </TableRow>
          </TableHead>
          <TableBody>
            {paginated.length === 0 ? (
              <TableRow>
                <TableCell colSpan={9} align="center" sx={{ py: 8 }}>
                  <EmptyIcon sx={{ fontSize: 56, color: 'text.disabled', mb: 1 }} />
                  <Typography color="text.secondary" gutterBottom>No clients found</Typography>
                  <Typography variant="body2" color="text.disabled">
                    Try different search terms or add a new client.
                  </Typography>
                </TableCell>
              </TableRow>
            ) : paginated.map(client => (
              <TableRow key={client.id} hover selected={selected.has(client.id)}>
                <TableCell padding="checkbox" onClick={e => e.stopPropagation()}>
                  <Checkbox size="small" checked={selected.has(client.id)}
                    onChange={() => toggleOne(client.id)} />
                </TableCell>
                <TableCell>
                  <Typography variant="body2" color="text.secondary"
                    fontFamily="monospace" fontSize={12}>
                    {fmtClientId(client.id)}
                  </Typography>
                </TableCell>
                {/* Clickable name link */}
                <TableCell>
                  <Box
                    component="span"
                    onClick={() => navigate(`/clients/${client.id}`)}
                    sx={{ cursor: 'pointer', '&:hover': { textDecoration: 'underline' } }}>
                    <Typography fontWeight={600} color="primary.main" component="span">
                      {client.first_name} {client.last_name}
                    </Typography>
                  </Box>
                  {client.company_name && (
                    <Typography variant="caption" color="text.secondary" display="block">
                      {client.company_name}
                    </Typography>
                  )}
                </TableCell>
                <TableCell>
                  <a href={`mailto:${client.email}`} onClick={e => e.stopPropagation()}
                    style={{ color: 'inherit', textDecoration: 'none' }}>
                    {client.email}
                  </a>
                </TableCell>
                <TableCell>
                  {client.phone_primary
                    ? <a href={`tel:${client.phone_primary}`} onClick={e => e.stopPropagation()}
                        style={{ color: 'inherit', textDecoration: 'none' }}>
                        {client.phone_primary}
                      </a>
                    : <Typography variant="body2" color="text.disabled">—</Typography>
                  }
                </TableCell>
                <TableCell>
                  <Typography variant="body2" color="text.secondary">
                    {fmtDate(client.created_at)}
                  </Typography>
                </TableCell>
                <TableCell sx={{ maxWidth: 200 }}>
                  <PetTags summary={client.pets_summary} />
                </TableCell>
                <TableCell>
                  <Chip
                    label={client.is_active === 1 ? 'Active' : 'Inactive'}
                    size="small"
                    sx={client.is_active === 1
                      ? { bgcolor: '#d1fae5', color: '#065f46', fontWeight: 600 }
                      : { bgcolor: '#fee2e2', color: '#991b1b', fontWeight: 600 }}
                  />
                </TableCell>
                <TableCell align="right" onClick={e => e.stopPropagation()}>
                  <RowMenu
                    onView={() => navigate(`/clients/${client.id}`)}
                    onEdit={() => openEdit(client)}
                    onDelete={() => setArchiveClient(client)}
                  />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        <TablePagination
          component="div"
          count={filtered.length}
          page={page}
          rowsPerPage={rowsPerPage}
          rowsPerPageOptions={[10, 25, 50]}
          onPageChange={(_, p) => setPage(p)}
          onRowsPerPageChange={e => { setRowsPerPage(parseInt(e.target.value)); setPage(0); }}
          labelDisplayedRows={({ from, to, count }) =>
            `Showing ${from}–${to} of ${count} client${count !== 1 ? 's' : ''}`}
        />
      </TableContainer>

      {/* Create dialog */}
      <Dialog open={createOpen} onClose={() => setCreateOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>New Client</DialogTitle>
        <DialogContent>
          {createError && <Alert severity="error" sx={{ mb: 1, mt: 1 }}>{createError}</Alert>}
          <ClientFormFields f={form} setF={setForm} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateOpen(false)} disabled={creating}>Cancel</Button>
          <Button variant="contained" onClick={handleCreate} disabled={creating}>
            {creating ? <CircularProgress size={20} /> : 'Create Client'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit dialog */}
      <Dialog open={!!editClient} onClose={() => setEditClient(null)} maxWidth="sm" fullWidth>
        <DialogTitle>
          Edit — {editClient?.first_name} {editClient?.last_name}
        </DialogTitle>
        <DialogContent>
          {editError && <Alert severity="error" sx={{ mb: 1, mt: 1 }}>{editError}</Alert>}
          <ClientFormFields f={editForm} setF={setEditForm} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditClient(null)} disabled={editSaving}>Cancel</Button>
          <Button variant="contained" onClick={handleEdit} disabled={editSaving}>
            {editSaving ? <CircularProgress size={20} /> : 'Save Changes'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Archive confirmation */}
      <Dialog open={!!archiveClient} onClose={() => setArchiveClient(null)}>
        <DialogTitle>Archive Client</DialogTitle>
        <DialogContent>
          <Typography gutterBottom>
            Are you sure? This will archive{' '}
            <strong>{archiveClient?.first_name} {archiveClient?.last_name}</strong>.
          </Typography>
          <Typography variant="body2" color="text.secondary">
            You cannot delete a client with active pets or outstanding balances.
            Archiving hides them from the active clients list but preserves all records.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setArchiveClient(null)}>Cancel</Button>
          <Button variant="contained" color="error" onClick={handleArchive} disabled={archiving}>
            {archiving ? 'Archiving...' : 'Archive Client'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Bulk delete confirmation */}
      <Dialog open={bulkDeleteOpen} onClose={() => setBulkDeleteOpen(false)}>
        <DialogTitle>Archive {selected.size} Client{selected.size !== 1 ? 's' : ''}?</DialogTitle>
        <DialogContent>
          <Typography>
            This will archive {selected.size} client{selected.size !== 1 ? 's' : ''}.
            They will no longer appear in the active clients list.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBulkDeleteOpen(false)}>Cancel</Button>
          <Button variant="contained" color="error" onClick={handleBulkDelete} disabled={bulkDeleting}>
            {bulkDeleting ? <CircularProgress size={20} /> : `Archive ${selected.size}`}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ClientsPage;
